import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  signal,
  viewChild,
  ViewChild,
  WritableSignal,
  Signal,
  model,
} from '@angular/core';
import { isNativeWindow } from '@local/common-web';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SCREEN_SIZE_MAX_WIDTH, windowSizeObserver } from '@shared/utils';
import { AssistantChatData } from '../../model';
import { ChatsService } from '../../services/chats.service';
import { delay } from '@local/ts-infra';
import { animate, style, transition, trigger } from '@angular/animations';
import { ClientStorageService } from '@shared/services/client-storage.service';
import { Chats } from '@local/client-contracts';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterService } from '@shared/services/router.service';
import { CHAT_PAGE_PATH } from 'src/app/bar/utils/constants';
import { map, pairwise, startWith, tap } from 'rxjs';
import { NgScrollbar } from 'ngx-scrollbar';
type SidebarState = 'pined' | 'unpinned';

@UntilDestroy()
@Component({
  selector: 'chat-sidebar',
  templateUrl: './chat-sidebar.component.html',
  styleUrls: ['./chat-sidebar.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [style({ opacity: 0, transform: 'translateX(0)' })]),
      transition(':leave', [
        animate('300ms ease-in', style({ transform: 'translateX(-100%)', opacity: 0 })), // Slides out smoothly
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatSidebarComponent {
  private readonly PAGE_OFFSET = 50;
  private readonly ANIMATION_TRANSITION_OPTIONS = '300ms ease-out';

  sidebarVisible = false;
  isNative = isNativeWindow();
  isSmallScreen = false;
  transitionOptions = this.ANIMATION_TRANSITION_OPTIONS;
  isPinned = model<boolean>(false);
  filterValue: WritableSignal<string> = signal('');
  page: WritableSignal<number> = signal(0);
  showScrollBar: WritableSignal<boolean> = signal(false);
  showAssistantsScrollBar: WritableSignal<boolean> = signal(false);
  currentTypingSessionId: WritableSignal<string> = signal(null);
  sessions: Signal<Chats.ChatSession[]>;
  assistantsForChat: Signal<{ [key: string]: AssistantChatData }>;
  currentAssistant: Signal<AssistantChatData>;
  currentSessionId: Signal<string>;

  scrollRef = viewChild<NgScrollbar>('mainScroll');
  assistantScrollRef = viewChild<NgScrollbar>('assistantsScroll');
  @ViewChild('AssistantItemTitle', { read: ElementRef }) AssistantItemTitleRef: ElementRef;

  assistantsContainerHeight = computed(() => {
    return `${Math.min(264, this.assistantsList()?.length * 40)}px`;
  });

  filterSessions = computed(() => {
    if (this.filterValue() || this.currentAssistant()?.isGlobalAssistant) {
      const result = [];
      for (const session of this.sessions()) {
        if (
          (session.name?.toLowerCase().includes(this.filterValue().toLocaleLowerCase()) ||
            this.assistantsList()
              ?.map((item) => item.id)
              .includes(session.assistantId)) &&
          (!this.currentAssistant()?.isGlobalAssistant || session.assistantId === this.currentAssistant().id)
        ) {
          result.push(session);
        }
        if (result.length === (this.page() + 1) * this.PAGE_OFFSET) {
          break;
        }
      }
      return result;
    }
    return this.sessions()?.slice(0, (this.page() + 1) * this.PAGE_OFFSET);
  });

  currentSession = computed(() => {
    return this.sessions()?.find((session) => session.id === this.currentSessionId());
  });

  groupedSessions = computed(() => {
    const sessions = this.filterSessions();
    if (!sessions) return;

    const relativeDates = this.getRelativeDates();
    const groups = new Map<string, any[]>();

    for (const item of sessions) {
      const itemDate = new Date(item.lastInteractTime);
      itemDate.setHours(0, 0, 0, 0);

      const key = this.getGroupKey(itemDate, relativeDates);

      if (!groups.has(key)) {
        groups.set(key, []);
      }

      groups.get(key)?.push(item);
    }

    return Array.from(groups.entries()).map(([label, items]) => ({
      label,
      items,
    }));
  });

  showAssistantList = computed(() => {
    return this.assistantsForChat() && Object.values(this.assistantsForChat())?.length > 1 && !this.currentAssistant()?.isGlobalAssistant;
  });

  assistantsList = computed(() => {
    if (!this.showAssistantList()) return;
    const sortAssistant = Object.values(this.assistantsForChat()).sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return a?.name?.localeCompare(b.name);
    });
    sortAssistant.push({ id: 'new_assistant', name: 'New Assistant' });
    if (this.filterValue()) {
      return sortAssistant?.filter(
        (assistant) => assistant.name.toLowerCase().includes(this.filterValue().toLocaleLowerCase()) || assistant.id === this.filterValue()
      );
    }
    return sortAssistant;
  });

  noResults = computed(() => {
    return !this.assistantsList()?.length && !this.groupedSessions()?.length && this.filterValue();
  });
  searchPlaceholder = computed(() => {
    if (!this.showAssistantList()) {
      return 'Search for thread';
    }
    return 'Search for thread or assistant';
  });

  trackByAssistant = (index: number, { id }: AssistantChatData): string => id;

  @Input() isBannerActive: boolean;

  @HostBinding('attr.bannerActive')
  get bannerActive() {
    return this.isBannerActive;
  }

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private chatsService: ChatsService,
    private clientStorage: ClientStorageService,
    private routerService: RouterService
  ) {
    this.sessions = toSignal(
      this.chatsService.sessions$.pipe(
        untilDestroyed(this),
        startWith<Chats.ChatSession[]>([]),
        pairwise<Chats.ChatSession[]>(),
        tap(([prev, curr]) => {
          if (prev?.length && this.currentSessionId()) {
            const prevCurrentSession = prev.find((item) => item.id === this.currentSessionId());
            if (prevCurrentSession && !prevCurrentSession.name) {
              const currCurrentSession = curr.find((item) => item.id === this.currentSessionId());
              if (currCurrentSession?.name) {
                this.currentTypingSessionId.set(this.currentSessionId());
              }
            }
          }
        }),
        map(([_, curr]) => {
          return curr.filter((thread) => !thread.ignore);
        })
      )
    );
    this.assistantsForChat = toSignal(this.chatsService.assistantsForChat$);
    this.currentAssistant = toSignal(this.chatsService.currentAssistant$);
    this.currentSessionId = toSignal(this.chatsService.currentSessionId$);

    effect(() => {
      this.scrollRef()
        ?.scrolled.pipe(untilDestroyed(this))
        .subscribe((event) => {
          this.onScroll(event);
        });
    });
    effect(() => {
      this.assistantScrollRef()
        ?.scrolled.pipe(untilDestroyed(this))
        .subscribe(() => {
          this.showAssistantsScrollBar.set(true);
        });
    });
  }

  ngOnInit() {
    this.getWindowSize();
  }

  getWindowSize() {
    windowSizeObserver()
      .pipe(untilDestroyed(this))
      .subscribe((value) => this.handleScreenSizeChange(value));
  }

  private handleScreenSizeChange(value: { width: number }) {
    const isSmallScreen = value.width <= SCREEN_SIZE_MAX_WIDTH.small;
    if (this.isSmallScreen !== isSmallScreen) {
      this.isSmallScreen = isSmallScreen;
      this.isPinned.set(false);
    }
  }

  private getRelativeDates(): { today: Date; yesterday: Date; sevenDaysAgo: Date; thirtyDaysAgo: Date } {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);

    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    return { today, yesterday, sevenDaysAgo, thirtyDaysAgo };
  }

  private getGroupKey(itemDate: Date, relativeDates: { today: Date; yesterday: Date; sevenDaysAgo: Date; thirtyDaysAgo: Date }): string {
    const { today, yesterday, sevenDaysAgo, thirtyDaysAgo } = relativeDates;

    if (itemDate.getTime() === today.getTime()) {
      return 'Today';
    }
    if (itemDate.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    }
    if (itemDate >= sevenDaysAgo) {
      return 'Previous 7 Days';
    }
    if (itemDate >= thirtyDaysAgo) {
      return 'Previous 30 Days';
    }
    if (itemDate.getFullYear() === today.getFullYear()) {
      return itemDate.toLocaleDateString('en-US', { month: 'long' });
    }
    return itemDate.getFullYear().toString();
  }

  closeSidebarIfUnpinned(): void {
    if (!this.isPinned()) {
      this.changeSidebarVisible(false);
    }
  }

  async changeSidebarVisible(visible: boolean, delayNeeded?: boolean) {
    if (delayNeeded) {
      await delay(0);
    }
    this.sidebarVisible = visible;
    this.cdr.markForCheck();
  }

  private updateTransitionOptionsBasedOnPinState(isPinned: boolean): void {
    this.transitionOptions = isPinned ? '0ms' : this.ANIMATION_TRANSITION_OPTIONS;
    this.cdr.markForCheck();
  }

  setSidebarPinnedState(state: boolean) {
    this.isPinned.set(state);
    this.clientStorage.set('chatSidebarState', state ? 'pinned' : 'unpinned');
    this.updateTransitionOptionsBasedOnPinState(state);
    this.changeSidebarVisible(false, true);
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    if (
      !this.elementRef.nativeElement.contains(event.target) &&
      this.sidebarVisible &&
      !this.isPinned() &&
      (event.target as HTMLElement).textContent !== 'Rename'
    ) {
      this.changeSidebarVisible(false);
    }
  }

  updateFilter(value) {
    this.filterValue.set(value);
  }

  onScroll(event: Event) {
    this.showScrollBar.set(true);
    const element = event.target as HTMLElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      this.page.update((page) => ++page);
    }
  }

  selectedSession({ item }) {
    this.closeSidebarIfUnpinned();
    this.navigateToSession(item);
  }

  navigateToSession(item): void {
    this.routerService.navigateByUrl(
      item?.assistantId ? `/${CHAT_PAGE_PATH}/${item?.assistantId}/c/${item?.id}` : `/${CHAT_PAGE_PATH}/c/${item?.id}`
    );
  }

  hideScrollbar() {
    this.showScrollBar.set(false);
  }

  hideAssistantScrollbar() {
    this.showAssistantsScrollBar.set(false);
  }
}
